import React, { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import ProblemSection from "@/components/landing/ProblemSection";
import InsightSection from "@/components/landing/InsightSection";
import PositioningSection from "@/components/landing/PositioningSection";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";
import SEO from "@/components/SEO";
import {
  SITE_NAME,
  SITE_URL,
  SITE_DESCRIPTION_CA,
  SITE_DESCRIPTION_ES,
  DEFAULT_OG_IMAGE,
  PUBLISHER_EMAIL,
  KNOWS_ABOUT_CA,
  KNOWS_ABOUT_ES,
} from "@/lib/seo";
import { useLanguage } from "@/lib/i18n";

export default function Landing() {
  const { lang } = useLanguage();

  useEffect(() => {
    // Simple intersection-observer based reveal
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in-view");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const title = lang === "es"
    ? "Recuperar capacidad, paso a paso"
    : "Recuperar capacitat, pas a pas";
  const description = lang === "es" ? SITE_DESCRIPTION_ES : SITE_DESCRIPTION_CA;
  const path = lang === "es" ? "/es" : "/";

  // Enriched Organization JSON-LD — site-wide brand markup with E-E-A-T signals
  const organizationSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}#organization`,
        name: SITE_NAME,
        alternateName: "AI Metabolic",
        url: SITE_URL,
        logo: {
          "@type": "ImageObject",
          url: DEFAULT_OG_IMAGE,
          width: 1200,
          height: 630,
        },
        description,
        inLanguage: lang === "es" ? "es-ES" : "ca-ES",
        foundingDate: "2026",
        areaServed: [
          { "@type": "Country", name: "España" },
          { "@type": "Country", name: "Andorra" },
        ],
        knowsAbout: lang === "es" ? KNOWS_ABOUT_ES : KNOWS_ABOUT_CA,
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          email: PUBLISHER_EMAIL,
          availableLanguage: ["Catalan", "Spanish"],
        },
        slogan: lang === "es"
          ? "Entender tu punto de partida antes de empezar."
          : "Entendre el teu punt de partida abans de començar.",
        sameAs: [],
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}#website`,
        url: SITE_URL,
        name: SITE_NAME,
        description,
        publisher: { "@id": `${SITE_URL}#organization` },
        inLanguage: lang === "es" ? "es-ES" : "ca-ES",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#0F172A] font-body" data-testid="landing-root">
      <SEO
        title={title}
        description={description}
        path={path}
        lang={lang}
        alternates={[
          { lang: "ca", path: "/" },
          { lang: "es", path: "/es" },
        ]}
        schema={organizationSchema}
      />
      <Navbar />
      <main>
        <Hero />
        <ProblemSection />
        <InsightSection />
        <PositioningSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
